// ==================== TEMPLATE CONSTANTS ====================
// WhatsApp Business API Template Configuration

import type { SelectOption } from '../components/ui/Select';
import { COUNTRIES } from './countries.constants';

// ==================== CATEGORY OPTIONS ====================

export const TEMPLATE_CATEGORY_OPTIONS: SelectOption[] = [
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'UTILITY', label: 'Utility' },
  { value: 'AUTHENTICATION', label: 'Authentication' },
];

// ==================== STATUS OPTIONS ====================

export const TEMPLATE_STATUS_OPTIONS: SelectOption[] = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PENDING_APPROVAL', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
];

export const TEMPLATE_FILTER_STATUS_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'All Status' },
  ...TEMPLATE_STATUS_OPTIONS,
];

export const TEMPLATE_FILTER_CATEGORY_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'All Categories' },
  ...TEMPLATE_CATEGORY_OPTIONS,
];

// ==================== HEADER TYPE OPTIONS ====================

export const TEMPLATE_HEADER_TYPE_OPTIONS: SelectOption[] = [
  { value: '', label: 'No Header' },
  { value: 'TEXT', label: 'Text' },
  { value: 'IMAGE', label: 'Image' },
  { value: 'VIDEO', label: 'Video' },
  { value: 'DOCUMENT', label: 'Document' },
];

// ==================== BUTTON OPTIONS ====================

// Quick Reply button types
export const TEMPLATE_QUICK_REPLY_TYPE_OPTIONS: SelectOption[] = [
  { value: 'QUICK_REPLY', label: 'Quick Reply' },
];

// Call to Action button types
export const TEMPLATE_CTA_TYPE_OPTIONS: SelectOption[] = [
  { value: 'URL', label: 'Visit website' },
  { value: 'PHONE', label: 'Call phone number' },
  { value: 'COPY_CODE', label: 'Copy offer code' },
];

// URL type options for website buttons
export const TEMPLATE_URL_TYPE_OPTIONS: SelectOption[] = [
  { value: 'STATIC', label: 'Static' },
  { value: 'DYNAMIC', label: 'Dynamic' },
];

// ==================== BUTTON LIMITS ====================

export const TEMPLATE_MAX_BUTTONS = 10;
export const TEMPLATE_BUTTON_LIST_THRESHOLD = 3; // Buttons appear in a list if more than this

// ==================== COUNTRY CODE OPTIONS ====================
// Country codes mapped from countries.constants.ts

export const TEMPLATE_COUNTRY_CODE_OPTIONS: SelectOption[] = COUNTRIES.map(country => ({
  value: country.dialCode,
  label: `${country.flag} ${country.dialCode}`,
}));

// ==================== LANGUAGE OPTIONS ====================
// Meta WhatsApp supported languages (as of Nov 4, 2025)

export const TEMPLATE_LANGUAGE_OPTIONS: SelectOption[] = [
  { value: 'af', label: 'Afrikaans' },
  { value: 'sq', label: 'Albanian' },
  { value: 'ar', label: 'Arabic' },
  { value: 'ar_EG', label: 'Arabic (EGY)' },
  { value: 'ar_AE', label: 'Arabic (UAE)' },
  { value: 'ar_LB', label: 'Arabic (LBN)' },
  { value: 'ar_MA', label: 'Arabic (MAR)' },
  { value: 'ar_QA', label: 'Arabic (QAT)' },
  { value: 'az', label: 'Azerbaijani' },
  { value: 'be_BY', label: 'Belarusian' },
  { value: 'bn', label: 'Bengali' },
  { value: 'bn_IN', label: 'Bengali (IND)' },
  { value: 'bg', label: 'Bulgarian' },
  { value: 'ca', label: 'Catalan' },
  { value: 'zh_CN', label: 'Chinese (CHN)' },
  { value: 'zh_HK', label: 'Chinese (HKG)' },
  { value: 'zh_TW', label: 'Chinese (TAI)' },
  { value: 'hr', label: 'Croatian' },
  { value: 'cs', label: 'Czech' },
  { value: 'da', label: 'Danish' },
  { value: 'prs_AF', label: 'Dari' },
  { value: 'nl', label: 'Dutch' },
  { value: 'nl_BE', label: 'Dutch (BEL)' },
  { value: 'en', label: 'English' },
  { value: 'en_GB', label: 'English (UK)' },
  { value: 'en_US', label: 'English (US)' },
  { value: 'en_AE', label: 'English (UAE)' },
  { value: 'en_AU', label: 'English (AUS)' },
  { value: 'en_CA', label: 'English (CAN)' },
  { value: 'en_GH', label: 'English (GHA)' },
  { value: 'en_IE', label: 'English (IRL)' },
  { value: 'en_IN', label: 'English (IND)' },
  { value: 'en_JM', label: 'English (JAM)' },
  { value: 'en_MY', label: 'English (MYS)' },
  { value: 'en_NZ', label: 'English (NZL)' },
  { value: 'en_QA', label: 'English (QAT)' },
  { value: 'en_SG', label: 'English (SGP)' },
  { value: 'en_UG', label: 'English (UGA)' },
  { value: 'en_ZA', label: 'English (ZAF)' },
  { value: 'et', label: 'Estonian' },
  { value: 'fil', label: 'Filipino' },
  { value: 'fi', label: 'Finnish' },
  { value: 'fr', label: 'French' },
  { value: 'fr_BE', label: 'French (BEL)' },
  { value: 'fr_CA', label: 'French (CAN)' },
  { value: 'fr_CH', label: 'French (CHE)' },
  { value: 'fr_CI', label: 'French (CIV)' },
  { value: 'fr_MA', label: 'French (MAR)' },
  { value: 'ka', label: 'Georgian' },
  { value: 'de', label: 'German' },
  { value: 'de_AT', label: 'German (AUT)' },
  { value: 'de_CH', label: 'German (CHE)' },
  { value: 'el', label: 'Greek' },
  { value: 'gu', label: 'Gujarati' },
  { value: 'ha', label: 'Hausa' },
  { value: 'he', label: 'Hebrew' },
  { value: 'hi', label: 'Hindi' },
  { value: 'hu', label: 'Hungarian' },
  { value: 'id', label: 'Indonesian' },
  { value: 'ga', label: 'Irish' },
  { value: 'it', label: 'Italian' },
  { value: 'ja', label: 'Japanese' },
  { value: 'kn', label: 'Kannada' },
  { value: 'kk', label: 'Kazakh' },
  { value: 'rw_RW', label: 'Kinyarwanda' },
  { value: 'ko', label: 'Korean' },
  { value: 'ky_KG', label: 'Kyrgyz (Kyrgyzstan)' },
  { value: 'lo', label: 'Lao' },
  { value: 'lv', label: 'Latvian' },
  { value: 'lt', label: 'Lithuanian' },
  { value: 'mk', label: 'Macedonian' },
  { value: 'ms', label: 'Malay' },
  { value: 'ml', label: 'Malayalam' },
  { value: 'mr', label: 'Marathi' },
  { value: 'nb', label: 'Norwegian' },
  { value: 'ps_AF', label: 'Pashto' },
  { value: 'fa', label: 'Persian' },
  { value: 'pl', label: 'Polish' },
  { value: 'pt_BR', label: 'Portuguese (BR)' },
  { value: 'pt_PT', label: 'Portuguese (POR)' },
  { value: 'pa', label: 'Punjabi' },
  { value: 'ro', label: 'Romanian' },
  { value: 'ru', label: 'Russian' },
  { value: 'sr', label: 'Serbian' },
  { value: 'si_LK', label: 'Sinhala' },
  { value: 'sk', label: 'Slovak' },
  { value: 'sl', label: 'Slovenian' },
  { value: 'es', label: 'Spanish' },
  { value: 'es_AR', label: 'Spanish (ARG)' },
  { value: 'es_CL', label: 'Spanish (CHL)' },
  { value: 'es_CO', label: 'Spanish (COL)' },
  { value: 'es_CR', label: 'Spanish (CRI)' },
  { value: 'es_DO', label: 'Spanish (DOM)' },
  { value: 'es_EC', label: 'Spanish (ECU)' },
  { value: 'es_HN', label: 'Spanish (HND)' },
  { value: 'es_MX', label: 'Spanish (MEX)' },
  { value: 'es_PA', label: 'Spanish (PAN)' },
  { value: 'es_PE', label: 'Spanish (PER)' },
  { value: 'es_ES', label: 'Spanish (SPA)' },
  { value: 'es_UY', label: 'Spanish (URY)' },
  { value: 'sw', label: 'Swahili' },
  { value: 'sv', label: 'Swedish' },
  { value: 'ta', label: 'Tamil' },
  { value: 'te', label: 'Telugu' },
  { value: 'th', label: 'Thai' },
  { value: 'tr', label: 'Turkish' },
  { value: 'uk', label: 'Ukrainian' },
  { value: 'ur', label: 'Urdu' },
  { value: 'uz', label: 'Uzbek' },
  { value: 'vi', label: 'Vietnamese' },
  { value: 'zu', label: 'Zulu' },
];

// ==================== EMOJI CATEGORIES ====================
// Common emojis for quick access in body text editor

export interface EmojiCategory {
  name: string;
  emojis: string[];
}

export const TEMPLATE_EMOJI_CATEGORIES: EmojiCategory[] = [
  {
    name: 'Smileys',
    emojis: ['😀', '😃', '😄', '😁', '😊', '🥰', '😍', '🤩', '😘', '😗', '😚', '😙', '🥲', '😋', '😛', '😜', '🤪', '😝', '🤗', '🤭', '😏', '😌', '😔', '😪', '🤤', '😴', '😷', '🤒', '🤕', '🤢', '🤮', '🤧', '🥵', '🥶', '🥴', '😵', '🤯', '🤠', '🥳', '🥸', '😎', '🤓', '🧐'],
  },
  {
    name: 'Gestures',
    emojis: ['👋', '🤚', '🖐️', '✋', '🖖', '👌', '🤌', '🤏', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '👍', '👎', '✊', '👊', '🤛', '🤜', '👏', '🙌', '🤝', '🙏'],
  },
  {
    name: 'Hearts',
    emojis: ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❤️‍🔥', '❤️‍🩹', '💕', '💞', '💓', '💗', '💖', '💘', '💝'],
  },
  {
    name: 'Objects',
    emojis: ['🎉', '🎊', '🎈', '🎁', '🏆', '🥇', '⭐', '🌟', '✨', '💫', '🔥', '💯', '✅', '❌', '⚠️', '📣', '📢', '🔔', '📱', '💻', '⌚', '📧', '✉️', '📦', '🛒', '💰', '💵', '💳', '🎯', '📍'],
  },
  {
    name: 'Animals',
    emojis: ['🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯', '🦁', '🐮', '🐷', '🐸', '🐵', '🐔', '🐧', '🐦', '🐤', '🦆', '🦅', '🦉', '🦇', '🐺', '🐗', '🐴', '🦄', '🐝', '🐛', '🦋', '🐌', '🐞', '🐜', '🦟', '🦗', '🕷️', '🦂', '🐢', '🐍', '🦎', '🦖', '🦕', '🐙', '🦑', '🦐', '🦞', '🦀', '🐡', '🐠', '🐟', '🐬', '🐳', '🐋', '🦈', '🐊', '🐅', '🐆', '🦓', '🦍', '🦧', '🐘', '🦛', '🦏', '🐪', '🐫', '🦒', '🦘', '🐃', '🐂', '🐄', '🐎', '🐖', '🐏', '🐑', '🦙', '🐐', '🦌', '🐕', '🐩', '🦮', '🐕‍🦺', '🐈', '🐈‍⬛', '🐓', '🦃', '🦚', '🦜', '🦢', '🦩', '🕊️', '🐇', '🦝', '🦨', '🦡', '🦫', '🦦', '🦥', '🐁', '🐀', '🐿️', '🦔'],
  },
];

// ==================== VARIABLE TYPES ====================
// Variable types supported by Meta WhatsApp templates

export interface VariableType {
  value: string;
  label: string;
  example: string;
}

export const TEMPLATE_VARIABLE_TYPES: VariableType[] = [
  { value: 'NUMBER', label: 'Number', example: '{{1}}' },
  { value: 'NAME', label: 'Name', example: '{{name}}' },
];

// ==================== BODY TEXT LIMITS ====================

export const TEMPLATE_BODY_MAX_LENGTH = 1024;
export const TEMPLATE_FOOTER_MAX_LENGTH = 60;
export const TEMPLATE_HEADER_TEXT_MAX_LENGTH = 60;
export const TEMPLATE_BUTTON_TEXT_MAX_LENGTH = 25;

// ==================== HELPER FUNCTIONS ====================

/**
 * Get language label by code
 */
export const getLanguageLabel = (code: string): string => {
  const option = TEMPLATE_LANGUAGE_OPTIONS.find(opt => opt.value === code);
  return option?.label || code;
};

/**
 * Get category label by value
 */
export const getCategoryLabel = (value: string): string => {
  const option = TEMPLATE_CATEGORY_OPTIONS.find(opt => opt.value === value);
  return option?.label || value;
};

/**
 * Get status label by value
 */
export const getStatusLabel = (value: string): string => {
  const option = TEMPLATE_STATUS_OPTIONS.find(opt => opt.value === value);
  return option?.label || value;
};

/**
 * Count number-type variables in template body text ({{1}}, {{2}}, etc.)
 */
export const countNumberVariables = (text: string): number => {
  return (text.match(/\{\{\d+\}\}/g) || []).length;
};

/**
 * Count name-type variables in template body text ({{name}}, {{email}}, etc.)
 */
export const countNameVariables = (text: string): number => {
  return (text.match(/\{\{[a-zA-Z_]+\}\}/g) || []).length;
};

/**
 * Count all variables in template body text
 */
export const countTemplateVariables = (text: string): number => {
  return countNumberVariables(text) + countNameVariables(text);
};

/**
 * Detect which variable type is currently in use
 * Returns 'NUMBER', 'NAME', or null if no variables
 */
export const detectVariableType = (text: string): 'NUMBER' | 'NAME' | null => {
  const hasNumberVars = countNumberVariables(text) > 0;
  const hasNameVars = countNameVariables(text) > 0;
  
  if (hasNumberVars) return 'NUMBER';
  if (hasNameVars) return 'NAME';
  return null;
};

/**
 * Get next available variable number
 */
export const getNextVariableNumber = (text: string): number => {
  const matches = text.match(/\{\{(\d+)\}\}/g) || [];
  if (matches.length === 0) return 1;
  
  const numbers = matches.map(m => parseInt(m.replace(/[{}]/g, '')));
  return Math.max(...numbers) + 1;
};

/**
 * Get list of named variables already used in text
 */
export const getUsedNamedVariables = (text: string): string[] => {
  const matches = text.match(/\{\{([a-zA-Z_]+)\}\}/g) || [];
  return matches.map(m => m.replace(/[{}]/g, ''));
};

/**
 * Extract all variables from text (both number and name types)
 * Returns array of unique variable strings like ['1', '2', 'name', 'email']
 */
export const extractAllVariables = (text: string): string[] => {
  // Match both {{1}}, {{2}} and {{name}}, {{email}}
  const matches = text.match(/\{\{([^{}]+)\}\}/g) || [];
  const variables = matches.map(m => m.replace(/[{}]/g, ''));
  // Return unique variables in order of appearance
  return [...new Set(variables)];
};

/**
 * Check if template body has variables at the start or end
 * Meta requires that variables cannot be at the start or end of the template
 * Returns { valid: boolean, error?: string }
 */
export const validateVariablePosition = (text: string): { valid: boolean; error?: string } => {
  if (!text || !text.trim()) {
    return { valid: true };
  }
  
  const trimmedText = text.trim();
  
  // Check if text starts with a variable
  if (/^\{\{[^{}]+\}\}/.test(trimmedText)) {
    return { 
      valid: false, 
      error: "Variables can't be at the start of the template." 
    };
  }
  
  // Check if text ends with a variable
  if (/\{\{[^{}]+\}\}$/.test(trimmedText)) {
    return { 
      valid: false, 
      error: "Variables can't be at the end of the template." 
    };
  }
  
  return { valid: true };
};
